import { useEffect, useState } from 'react'
import nexusApi from '../../services/nexusApi'
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts'
import { DollarSign, TrendingUp, TrendingDown, Users, AlertCircle, Clock, ChevronRight } from 'lucide-react'

const NX = {
  bg: '#0D1117', surface: '#161B22', surface2: '#1C2330', border: '#2A3441',
  text: '#E6EDF3', textDim: '#8B949E', accent: '#0066FF',
  green: '#3DB54A', amber: '#F5921E', red: '#EF4444',
}

const PLAN_COLORS: Record<string, string> = {
  trial: NX.textDim, standard: '#6366F1', professional: NX.accent, enterprise: NX.green,
}

function Card({ children, style = {} }: any) {
  return (
    <div style={{
      background: NX.surface, border: `1px solid ${NX.border}`,
      borderRadius: 12, padding: '1.25rem', ...style,
    }}>
      {children}
    </div>
  )
}

function Stat({ label, value, sub, color = NX.text, trend }: any) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ color: NX.textDim, fontSize: '0.8125rem' }}>{label}</div>
      <div style={{ color, fontSize: '1.75rem', fontWeight: 700, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ color: NX.textDim, fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 4 }}>
        {trend === 'up' && <TrendingUp size={12} color={NX.green} />}
        {trend === 'down' && <TrendingDown size={12} color={NX.red} />}
        {sub}
      </div>}
    </div>
  )
}

export default function NexusRevenue() {
  const [overview, setOverview] = useState<any>(null)
  const [trend, setTrend] = useState<any[]>([])
  const [byPlan, setByPlan] = useState<any[]>([])
  const [trials, setTrials] = useState<any>(null)
  const [failed, setFailed] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      nexusApi.get('/revenue/overview'),
      nexusApi.get('/revenue/mrr-trend'),
      nexusApi.get('/revenue/by-plan'),
      nexusApi.get('/revenue/trials-pipeline'),
      nexusApi.get('/revenue/failed-payments'),
    ]).then(([ov, tr, bp, tp, fp]) => {
      setOverview(ov.data)
      setTrend(tr.data.trend || [])
      setByPlan(bp.data.breakdown || [])
      setTrials(tp.data)
      setFailed(fp.data.failedPayments || [])
    }).catch(() => {}).finally(() => setLoading(false))
  }, [])

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: NX.textDim }}>
      Loading revenue data…
    </div>
  )

  const fmt = (n: number) => n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${n}`

  return (
    <div style={{ padding: '2rem', maxWidth: 1200 }}>
      <div style={{ marginBottom: '1.75rem' }}>
        <h1 style={{ color: NX.text, fontSize: '1.375rem', fontWeight: 700, margin: 0 }}>Revenue</h1>
        <p style={{ color: NX.textDim, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>Financial metrics and subscription pipeline</p>
      </div>

      {/* Hero stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        <Card>
          <Stat label="MRR" value={overview ? fmt(overview.mrr) : '—'} sub={`ARR: ${overview ? fmt(overview.arr) : '—'}`} color={NX.green} trend="up" />
        </Card>
        <Card>
          <Stat label="Paying Tenants" value={overview?.totalPaying ?? '—'} sub={`${overview?.totalTrials ?? 0} on trial`} color={NX.accent} />
        </Card>
        <Card>
          <Stat label="Churn Rate (30d)" value={`${overview?.churnRate ?? 0}%`} sub={`${overview?.churned30d ?? 0} tenants churned`} color={overview?.churnRate > 5 ? NX.red : NX.green} trend={overview?.churnRate > 5 ? 'down' : 'up'} />
        </Card>
        <Card>
          <Stat label="Avg Revenue / Tenant" value={overview ? fmt(Math.round(overview.arpt)) : '—'} sub={`Trial conversion: ${overview?.trialConversionRate ?? 0}%`} color={NX.amber} />
        </Card>
      </div>

      {/* MRR Trend chart */}
      <Card style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
          <h2 style={{ color: NX.text, fontSize: '1rem', fontWeight: 600, margin: 0 }}>MRR Trend — Last 12 Months</h2>
          <div style={{ color: NX.textDim, fontSize: '0.75rem' }}>Monthly Recurring Revenue</div>
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={trend}>
            <defs>
              <linearGradient id="mrrGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={NX.accent} stopOpacity={0.3} />
                <stop offset="95%" stopColor={NX.accent} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={NX.border} />
            <XAxis dataKey="month" tick={{ fill: NX.textDim, fontSize: 11 }} tickLine={false} />
            <YAxis tick={{ fill: NX.textDim, fontSize: 11 }} tickLine={false} tickFormatter={(v) => `$${v}`} />
            <Tooltip
              contentStyle={{ background: NX.surface2, border: `1px solid ${NX.border}`, borderRadius: 8, color: NX.text }}
              formatter={(v: any) => [`$${v}`, 'MRR']}
            />
            <Area type="monotone" dataKey="mrr" stroke={NX.accent} strokeWidth={2} fill="url(#mrrGrad)" />
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Revenue by Plan */}
        <Card>
          <h2 style={{ color: NX.text, fontSize: '1rem', fontWeight: 600, margin: '0 0 1.25rem' }}>Revenue by Plan</h2>
          {byPlan.length === 0 ? (
            <div style={{ color: NX.textDim, fontSize: '0.875rem', textAlign: 'center', padding: '2rem 0' }}>No paid plans yet</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={byPlan} dataKey="mrr" nameKey="plan" cx="50%" cy="50%" outerRadius={60} label={({ plan, mrr }) => mrr > 0 ? plan : ''}>
                    {byPlan.map((entry) => (
                      <Cell key={entry.plan} fill={PLAN_COLORS[entry.plan] || NX.accent} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: NX.surface2, border: `1px solid ${NX.border}`, borderRadius: 8, color: NX.text }}
                    formatter={(v: any) => [`$${v}`, 'MRR']}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                {byPlan.map(row => (
                  <div key={row.plan} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 10, height: 10, borderRadius: 2, background: PLAN_COLORS[row.plan] || NX.accent }} />
                      <span style={{ color: NX.text, fontSize: '0.875rem', textTransform: 'capitalize' }}>{row.plan}</span>
                      <span style={{ color: NX.textDim, fontSize: '0.75rem' }}>{row.count} tenants</span>
                    </div>
                    <span style={{ color: NX.green, fontWeight: 600, fontSize: '0.875rem' }}>${row.mrr}/mo</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>

        {/* Trial pipeline */}
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <h2 style={{ color: NX.text, fontSize: '1rem', fontWeight: 600, margin: 0 }}>Trial Pipeline</h2>
            <span style={{ background: `${NX.amber}22`, color: NX.amber, fontSize: '0.75rem', fontWeight: 600, padding: '2px 8px', borderRadius: 99 }}>
              {trials?.summary?.total ?? 0} active
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: '1rem' }}>
            {[
              { label: 'Expiring 7d', value: trials?.summary?.expiring7d ?? 0, color: NX.red },
              { label: 'Expiring 30d', value: trials?.summary?.expiring30d ?? 0, color: NX.amber },
              { label: 'High Engagement', value: trials?.summary?.highEngagement ?? 0, color: NX.green },
              { label: 'Total', value: trials?.summary?.total ?? 0, color: NX.accent },
            ].map(item => (
              <div key={item.label} style={{ background: NX.surface2, borderRadius: 8, padding: '0.75rem', textAlign: 'center' }}>
                <div style={{ color: item.color, fontWeight: 700, fontSize: '1.375rem', lineHeight: 1 }}>{item.value}</div>
                <div style={{ color: NX.textDim, fontSize: '0.6875rem', marginTop: 4 }}>{item.label}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 200, overflowY: 'auto' }}>
            {(trials?.trials || []).slice(0, 8).map((t: any) => (
              <div key={t.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '6px 8px', borderRadius: 6, background: NX.surface2,
              }}>
                <div>
                  <div style={{ color: NX.text, fontSize: '0.8125rem', fontWeight: 500 }}>{t.name}</div>
                  <div style={{ color: NX.textDim, fontSize: '0.6875rem' }}>{t.user_count} users · {t.incident_count} incidents</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{
                    fontSize: '0.6875rem', fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                    background: t.engagement_score >= 60 ? `${NX.green}22` : t.engagement_score >= 30 ? `${NX.amber}22` : `${NX.red}22`,
                    color: t.engagement_score >= 60 ? NX.green : t.engagement_score >= 30 ? NX.amber : NX.red,
                  }}>
                    {t.engagement_score}%
                  </div>
                  {t.plan_expires_at && (
                    <div style={{ color: NX.textDim, fontSize: '0.6875rem', marginTop: 2, display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Clock size={9} /> {new Date(t.plan_expires_at).toLocaleDateString('en-AU')}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Failed payments */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1rem' }}>
          <AlertCircle size={16} color={failed.length > 0 ? NX.red : NX.textDim} />
          <h2 style={{ color: NX.text, fontSize: '1rem', fontWeight: 600, margin: 0 }}>
            Past Due / Failed Payments
          </h2>
          {failed.length > 0 && (
            <span style={{ background: `${NX.red}22`, color: NX.red, fontSize: '0.75rem', fontWeight: 600, padding: '2px 8px', borderRadius: 99 }}>
              {failed.length}
            </span>
          )}
        </div>
        {failed.length === 0 ? (
          <div style={{ color: NX.textDim, fontSize: '0.875rem', textAlign: 'center', padding: '1.5rem 0' }}>
            No past due accounts
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${NX.border}` }}>
                {['Company', 'Plan', 'Expires', 'Note'].map(h => (
                  <th key={h} style={{ color: NX.textDim, fontSize: '0.75rem', fontWeight: 600, textAlign: 'left', padding: '0.5rem 0.75rem' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {failed.map((f: any) => (
                <tr key={f.id} style={{ borderBottom: `1px solid ${NX.border}` }}>
                  <td style={{ padding: '0.75rem', color: NX.text, fontSize: '0.875rem' }}>{f.name}</td>
                  <td style={{ padding: '0.75rem', color: NX.textDim, fontSize: '0.875rem', textTransform: 'capitalize' }}>{f.plan}</td>
                  <td style={{ padding: '0.75rem', color: NX.red, fontSize: '0.875rem' }}>
                    {f.plan_expires_at ? new Date(f.plan_expires_at).toLocaleDateString('en-AU') : '—'}
                  </td>
                  <td style={{ padding: '0.75rem', color: NX.textDim, fontSize: '0.75rem' }}>{f.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  )
}
